import { ReactNode, createContext, useContext, useMemo } from 'react'

import { AssetServiceClient } from '@droidmap/asset-service-contract'
import { createServiceEndpoint } from '@droidmap/frontend-env'
import { GeometryServiceClient } from '@droidmap/geometry-service-contract'
import { MappingJobServiceClient } from '@droidmap/mapping-job-service-contract'
import { OrganisationServiceClient } from '@droidmap/organisation-service-contract'
import { PermissionsServiceClient } from '@droidmap/permissions-service-contract'
import { ProjectServiceClient } from '@droidmap/project-service-contract'
import {
  ServiceClient,
  ServiceClientContext,
  StandardOrgIds,
  StandardUserIds,
} from '@droidmap/shared-service-contract'
import { TileServiceClient } from '@droidmap/tile-service-contract'
import { TokenServiceClient } from '@droidmap/token-service-contract'
import { UserServiceClient } from '@droidmap/user-service-contract'

import { useAuth } from './AuthContext.js'
import { useConfig } from './ConfigContext.js'

const ClientContext = createContext<Client | null>(null)

export const useClient = (): Client => {
  const context = useContext(ClientContext)
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider')
  }
  return context
}

interface ClientProviderProps {
  children: ReactNode
}
type ServiceClientConstructor<T extends ServiceClient> = new (
  config: ServiceClientContext,
) => T

export type Client = {
  organisationClient: OrganisationServiceClient
  projectClient: ProjectServiceClient
  assetClient: AssetServiceClient
  userClient: UserServiceClient
  mappingJobClient: MappingJobServiceClient
  tileClient: TileServiceClient
  tokenClient: TokenServiceClient
  permissionsClient: PermissionsServiceClient
  geometryClient: GeometryServiceClient
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const { ENV } = useConfig()
  const auth = useAuth()

  const createClient = useMemo(
    () =>
      <T extends ServiceClient>(
        ServiceClientConstructor: ServiceClientConstructor<T>,
        serviceName: string,
      ): T => {
        return new ServiceClientConstructor({
          getToken: auth.getAccessToken,
          endpoint: createServiceEndpoint(serviceName, ENV),
          droidMapMetadata: {
            userId: auth.userDetails.id,
            // TODO: Remove this once we have updated backend permissions
            // This is currently used as an initial value, all clients will update the metadata
            orgId: StandardOrgIds.SYSTEM,
          },
        })
      },
    [ENV, auth],
  )

  const clients = useMemo<Client | null>(() => {
    if (!auth.isAuthenticated || !auth.userDetails.id) {
      return {} as Client // necessary to ensure child context consumers don't break
    }

    return {
      organisationClient: createClient(
        OrganisationServiceClient,
        'organisation',
      ),
      projectClient: createClient(ProjectServiceClient, 'project'),
      assetClient: createClient(AssetServiceClient, 'asset'),
      userClient: createClient(UserServiceClient, 'user'),
      mappingJobClient: createClient(MappingJobServiceClient, 'mapping-job'),
      tileClient: createClient(TileServiceClient, 'tile'),
      tokenClient: createClient(TokenServiceClient, 'token'),
      permissionsClient: createClient(PermissionsServiceClient, 'permissions'),
      geometryClient: createClient(GeometryServiceClient, 'geometry'),
    }
  }, [createClient, auth])

  return (
    <ClientContext.Provider value={clients}>{children}</ClientContext.Provider>
  )
}

// Standalone functions for use outside of React components
export const createStandaloneOrganisationClient = (
  getToken: () => Promise<string>,
  userId: string,
  orgId: string,
  env: string,
): OrganisationServiceClient => {
  return new OrganisationServiceClient({
    getToken,
    endpoint: createServiceEndpoint('organisation', env),
    droidMapMetadata: { userId, orgId },
  })
}

export const createStandaloneUserServiceClient = (
  getToken: () => Promise<string>,
  userId: string,
  orgId: string,
  env: string,
): UserServiceClient => {
  return new UserServiceClient({
    getToken,
    endpoint: createServiceEndpoint('user', env),
    droidMapMetadata: { userId, orgId },
  })
}

/**
 * Anonymous asset-service client for the PUBLIC share-download page (#64).
 * The `/share/*` routes are unauthenticated — the share-link token in the
 * URL path is the credential — so the bearer token is empty and the metadata
 * identifiers are the system sentinels (the backend derives the real
 * org/user attribution from the share-link row).
 */
export const createPublicAssetClient = (env: string): AssetServiceClient => {
  return new AssetServiceClient({
    getToken: async () => '',
    endpoint: createServiceEndpoint('asset', env),
    droidMapMetadata: {
      userId: StandardUserIds.SYSTEM,
      orgId: StandardOrgIds.SYSTEM,
    },
  })
}
